/**
 * Client-side bundle freshness detection.
 *
 * On first call, fetches `/version.json` to capture the version of the bundle
 * the user originally loaded. If the manifest is unavailable (dev mode, 404,
 * malformed payload), polling is disabled for the lifetime of the tab. When
 * a poll observes a different version, an internal flag is latched on; the
 * router-level gate offers the update on the next navigation. Reloading is
 * always an explicit user action so returning to the app cannot discard work.
 */

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const VERSION_URL = '/version.json';

export interface VersionManifest {
  version: string;
  buildTime?: string;
}

interface VersionCheckState {
  started: boolean;
  manifest: VersionManifest | null;
  initialVersion: string | null;
  updatePending: boolean;
  intervalId: ReturnType<typeof setInterval> | null;
  visibilityHandler: (() => void) | null;
}

const state: VersionCheckState = {
  started: false,
  manifest: null,
  initialVersion: null,
  updatePending: false,
  intervalId: null,
  visibilityHandler: null,
};

let initialManifestPromise: Promise<VersionManifest | null> | null = null;

function isVersionManifest(value: unknown): value is VersionManifest {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.version === 'string' && candidate.version.length > 0;
}

async function fetchManifest(): Promise<VersionManifest | null> {
  try {
    const response = await fetch(VERSION_URL, { cache: 'no-store' });
    if (!response.ok) return null;
    const payload: unknown = await response.json();
    if (!isVersionManifest(payload)) return null;
    return payload;
  } catch {
    return null;
  }
}

async function checkForUpdate(): Promise<void> {
  if (state.updatePending || state.initialVersion === null) return;
  const manifest = await fetchManifest();
  if (manifest === null) return;
  if (manifest.version !== state.initialVersion) {
    state.updatePending = true;
    teardownPolling();
  }
}

function teardownPolling(): void {
  if (state.intervalId !== null) {
    clearInterval(state.intervalId);
    state.intervalId = null;
  }
  if (state.visibilityHandler !== null && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', state.visibilityHandler);
    state.visibilityHandler = null;
  }
}

function setupPolling(): void {
  state.intervalId = setInterval(() => {
    void checkForUpdate();
  }, POLL_INTERVAL_MS);

  if (typeof document !== 'undefined') {
    const handler = (): void => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate();
      }
    };
    state.visibilityHandler = handler;
    document.addEventListener('visibilitychange', handler);
  }
}

/**
 * Memoized loader for the initial bundle manifest. Resolves to the same
 * value (or `null` for dev/misconfigured deploys) for the lifetime of the
 * tab, regardless of how many callers ask for it. UI consumers that want
 * to display version metadata should await this.
 */
export function loadVersionManifest(): Promise<VersionManifest | null> {
  if (initialManifestPromise === null) {
    initialManifestPromise = fetchManifest().then((manifest) => {
      state.manifest = manifest;
      return manifest;
    });
  }
  return initialManifestPromise;
}

/**
 * Synchronous accessor for the initial manifest. Returns `null` until
 * `loadVersionManifest()` has resolved (or if the manifest is unavailable).
 */
export function getVersionManifest(): VersionManifest | null {
  return state.manifest;
}

export function startVersionCheck(): void {
  if (state.started) return;
  state.started = true;

  void loadVersionManifest().then((manifest) => {
    if (manifest === null) {
      // Dev mode or misconfigured deploy: silently disable polling forever.
      return;
    }
    state.initialVersion = manifest.version;
    setupPolling();
  });
}

export function isUpdatePending(): boolean {
  return state.updatePending;
}
